import React from 'react';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { Button, BackButton } from '../components/common';
import { useNavigate } from 'react-router-dom';

const ReleaseNotes = () => {
  const navigate = useNavigate();

  const releases = [
    {
      version: "v3.0.0",
      date: "January 2025",
      title: "Major Release: Complete Platform Enhancement - Simulation, Dashboard, ELO & Categorisation",
      highlights: [
        {
          title: "🎯 Player Categorisation System",
          description: "Revolutionary ELO-based player categorisation to prevent large skill gaps in matchmaking.",
          features: [
            "Dynamic player categorisation into skill tiers (A, B, C, D) based on session ELO distribution",
            "Separate categorisation for Mixed Doubles and Levels (gender-specific) game types",
            "Adaptive categorisation algorithm that adjusts to each session's player population",
            "Natural breaks algorithm with configurable thresholds (75 ELO gap, 100 ELO max spread)",
            "Minimum category size of 2 players and maximum of 4 categories to prevent over-segmentation",
            "Category restrictions enforced in matchmaking with hard filters and penalties",
            "Categories only recalculate when significant ELO changes occur (±50 points cumulative) or manual refresh",
            "Auto-refresh player categories at games-played milestones (every 3 games)"
          ]
        },
        {
          title: "📊 Player Ranking Dashboard",
          description: "Comprehensive visualisation of player categories and ELO ratings for session hosts.",
          features: [
            "Dedicated Player Ranking tab with separate views for Mixed, Men's Levels, and Women's Levels",
            "Visual category display with clear borders between skill tiers",
            "Summary chips showing ELO ranges for each category",
            "Toggle to show/hide uncategorized players",
            "Gender-specific text colors for easy player identification",
            "Sorted by category (A-D) then ELO for clear hierarchy"
          ]
        },
        {
          title: "🎮 Session Simulation Tool",
          description: "Powerful new tool for testing matchmaking and analyzing session outcomes before they happen.",
          features: [
            "Complete session simulation with realistic game progression",
            "Player filtering and category-based analysis",
            "Game ratings and balance evaluation",
            "Summary dashboard with comprehensive statistics",
            "Consecutive interaction blocking in matchmaking",
            "Visual game history and player interaction tracking"
          ]
        },
        {
          title: "👤 Enhanced Player Dashboard",
          description: "Completely redesigned player profile experience with comprehensive performance tracking.",
          features: [
            "New player profile page with tabbed interface (Performance, Partnerships, Account)",
            "Session performance modal with detailed game history",
            "Recent sessions list with quick access to past performance",
            "Gender-based coloring in session rankings for better visibility",
            "Refined recent form display with improved data visualization",
            "Enhanced player summary with comprehensive statistics"
          ]
        },
        {
          title: "📈 Advanced ELO Rating System",
          description: "Intelligent score-based ELO adjustments for balanced games with transparent breakdown.",
          features: [
            "Score-based ELO adjustments for balanced doubles games only",
            "Strict balance criteria: all players within 100 points (men's/women's) or ±50 for mixed",
            "Banded scoreline adjustments: 1-2 (±2), 3-5 (±4), 6-10 (±8), 11-21 (±12)",
            "ELO breakdown display showing base change + score adjustment",
            "Visual tooltip showing total, base, and score components",
            "Unbalanced games use standard win/loss ELO only"
          ]
        },
        {
          title: "⚙️ Session Configuration Enhancements",
          description: "Player categorisation setting integrated into all session creation workflows.",
          features: [
            "Enable/disable categorisation toggle in session creation forms",
            "Template-based categorisation settings that persist across sessions",
            "Custom session configuration with categorisation option",
            "Database persistence of categorisation preference per session",
            "Improved checkbox UI with prominent styling for better visibility",
            "Settings persist across page refreshes"
          ]
        },
        {
          title: "🔧 Enhanced Matchmaking Integration",
          description: "Category restrictions seamlessly integrated into the two-stage matchmaking algorithm.",
          features: [
            "Category compatibility checks in matchmaking scoring",
            "Hard filter for >1 tier gap (prevents A+D pairings)",
            "Penalty system for adjacent tier pairings (A+B, B+C, etc.)",
            "Category constraints applied after partnerships and scheduled games",
            "Simplified relaxation rules aligned with host game type settings",
            "Reduced logging noise with debug flag for cleaner console output"
          ]
        },
        {
          title: "⏱️ Session Management Enhancements",
          description: "Improved real-time session management with better controls and visibility.",
          features: [
            "Court timer component for tracking game duration",
            "Scheduled games auto-promote functionality",
            "Cooldown timing fixes for better player rotation",
            "Larger and more visible 'Next On Court' banner",
            "Cancelled and no-show status management for day-of cancellations"
          ]
        },
        {
          title: "🔍 Session Wrap-Up Improvements",
          description: "Enhanced session review with better filtering and performance optimizations.",
          features: [
            "Player search filter for games and interactions tables",
            "Fixed infinite fetch loops and performance issues",
            "Removed rating and relaxation columns for cleaner display",
            "Stabilized filters to prevent unnecessary re-renders",
            "Improved table loading and data synchronization"
          ]
        },
        {
          title: "👥 Player Management Updates",
          description: "Better control and visibility for managing players during active sessions.",
          features: [
            "Cancelled status for day-of cancellations",
            "No-show tab and status management",
            "Auto-sync between session_signups and session_players for active sessions",
            "Disabled controls in signup modal when session has started",
            "Prevented invalid status changes (e.g., playing to cancelled)",
            "Mark all as playing excludes cancelled/no-show players"
          ]
        },
        {
          title: "🏢 Club Management QoL",
          description: "Quality of life improvements for club administrators.",
          features: [
            "Consistent button styling across club management pages",
            "Enhanced club admin interface",
            "Improved club management workflows",
            "Better visual consistency throughout the application"
          ]
        },
        {
          title: "🔐 Account & Security",
          description: "Improved account management and password reset functionality.",
          features: [
            "Fixed account settings embedding to properly update current user",
            "Enhanced password reset flow with better security",
            "Improved user profile synchronization",
            "Better error handling for account operations"
          ]
        },
        {
          title: "🗄️ Database & Migration Updates",
          description: "New database fields and migrations for new features.",
          features: [
            "Added enable_player_categorisation field to sessions and club_session_setup tables",
            "Migration files properly numbered and sequenced",
            "Backfill existing records with default values",
            "Database migration for cancelled status support",
            "Comprehensive database comments documenting new fields"
          ]
        },
        {
          title: "🐛 Bug Fixes & Performance",
          description: "Critical bug fixes and performance optimizations across the application.",
          features: [
            "Fixed session wrap-up tables infinite fetch loops",
            "Resolved variable initialization order issues",
            "Improved React hook dependencies and memoization",
            "Enhanced error handling and user feedback",
            "Reduced logging noise with debug flags for cleaner console output"
          ]
        }
      ]
    },
    {
      version: "v2.0.0",
      date: "January 2025",
      title: "Major Release: Enhanced Session Management & Scheduled Games",
      highlights: [
        {
          title: "📅 Scheduled Games System",
          description: "Revolutionary pre-arranged match scheduling with intelligent matchmaking integration.",
          features: [
            "Create and manage scheduled games with custom player selection",
            "Drag-and-drop reordering of scheduled games with FIFO priority",
            "Visual indicators for players already in scheduled games",
            "Invalid game highlighting when players become unavailable",
            "Automatic integration with matchmaking algorithm for fair selection",
            "Scheduled games receive bonus scoring in matchmaking evaluation",
            "Limit of 5 scheduled games per session for optimal management"
          ]
        },
        {
          title: "🎯 Enhanced Session Creation Flow",
          description: "Completely redesigned session creation with dedicated pages and improved user experience.",
          features: [
            "Dedicated session pages with proper URL routing (/session/{sessionId})",
            "Custom session configuration modal with essential field collection",
            "Session name customization for better organization",
            "Template-based session creation with setup selection",
            "Improved navigation flow with proper back button handling",
            "Fixed season ID association for all new sessions"
          ]
        },
        {
          title: "🔧 Advanced Matchmaking Integration",
          description: "Scheduled games seamlessly integrate with the existing matchmaking system.",
          features: [
            "Scheduled games evaluated alongside regular matchmaking combinations",
            "Partnership-aware scheduled game validation",
            "Game type balance enforcement for scheduled matches",
            "Automatic cleanup when scheduled games are promoted to courts",
            "Player flow optimization for scheduled game participants"
          ]
        },
        {
          title: "🎨 UI/UX Improvements",
          description: "Enhanced user interface with consistent design patterns and improved navigation.",
          features: [
            "Reusable BackButton component for consistent navigation",
            "Improved modal designs with better form validation",
            "Enhanced player search functionality with filtering",
            "Better visual hierarchy and component organization",
            "Streamlined session management interface"
          ]
        },
        {
          title: "🐛 Critical Bug Fixes",
          description: "Resolved major issues with session creation and data integrity.",
          features: [
            "Fixed season ID not being set in database for new sessions",
            "Resolved End Session navigation to redirect to session list",
            "Fixed import issues with modal components",
            "Improved error handling and user feedback",
            "Enhanced data validation and form submission"
          ]
        }
      ]
    },
    {
      version: "v1.4.0",
      date: "January 2025",
      title: "Dynamic Court Management & Session Journey Improvements",
      highlights: [
        {
          title: "🏸 Dynamic Court Count Limits",
          description: "Intelligent court management based on available players.",
          features: [
            "Dynamic max courts calculation: Math.floor(availablePlayers / 4)",
            "Smart + button validation (disabled when insufficient players)",
            "Real-time tooltips showing player count vs requirements",
            "Automatic court limit updates as players join/leave session",
            "Prevents creating courts without enough players for matches"
          ]
        },
        {
          title: "🎯 Enhanced Session Start Journey",
          description: "Streamlined user experience for starting badminton sessions.",
          features: [
            "Redesigned /session page as active session dashboard",
            "Added 'Start Session' buttons to homepage club cards",
            "General 'Start Session' button in Quick Actions section",
            "Club-specific session selection with role-based access",
            "Improved session time display (HH:MM format without seconds)"
          ]
        },
        {
          title: "🔧 Court Management Improvements",
          description: "Better court assignment and removal logic.",
          features: [
            "Smart court removal logic (prioritizes empty courts)",
            "Fixed court numbering on removal (no renumbering)",
            "Enhanced court count controller with player-based validation",
            "Improved court assignment synchronization",
            "Better handling of court state changes"
          ]
        },
        {
          title: "🐛 Bug Fixes & Improvements",
          description: "Resolved issues with court management and UI components.",
          features: [
            "Fixed dropdown clipping issues with portal-based rendering",
            "Improved court removal validation logic",
            "Enhanced debugging for court count adjustments",
            "Better error handling for court operations",
            "Cleaned up court management state synchronization"
          ]
        }
      ]
    },
    {
      version: "v1.3.1",
      date: "January 2025",
      title: "Active Session Bug Fixes & Player Management Improvements",
      highlights: [
        {
          title: "🏸 Enhanced Player Management",
          description: "Comprehensive improvements to player status handling and session management.",
          features: [
            "Implemented separate paused list for better player organization",
            "Fixed player status synchronization across all components",
            "Added pause indicators for paused players in waiting list",
            "Set 'All' tab as default in player management for better UX",
            "Fixed 'Not Signed Up' status handling and dropdown options"
          ]
        },
        {
          title: "🎯 Improved Matchmaking Logic",
          description: "Enhanced court assignment and match generation to prevent repetitive games.",
          features: [
            "Prevented endless same games by requiring >4 players for auto-assignment",
            "Added informational messages when exactly 4 players are waiting",
            "Improved match variety by waiting for proper player rotation",
            "Enhanced court rotation logic for better player mixing"
          ]
        },
        {
          title: "🔧 UI/UX Improvements",
          description: "Better user experience with cleaner notifications and interface.",
          features: [
            "Replaced complex tooltips with simple notification messages",
            "Removed duplicate notifications from waiting list section",
            "Fixed information icon cut-off issues",
            "Improved notification positioning and visibility",
            "Cleaned up player management interface"
          ]
        },
        {
          title: "🐛 Bug Fixes",
          description: "Resolved multiple issues with player status management and UI components.",
          features: [
            "Fixed bulk player status changes not updating waiting list",
            "Fixed individual player status changes not syncing properly",
            "Fixed paused players disappearing from waiting list during matchmaking",
            "Fixed 'Finished' status handling in player management",
            "Removed unused green plus buttons from player cards"
          ]
        }
      ]
    },
    {
      version: "v1.3.0",
      date: "January 2025",
      title: "Session Management UI/UX Overhaul",
      highlights: [
        {
          title: "🎯 Streamlined Session Navigation",
          description: "Redesigned session interface with improved navigation and user experience.",
          features: [
            "Removed redundant 'Players' tab - player management now always visible at bottom",
            "Added placeholder tabs for future features: Player Stats, Session Ranking, Games",
            "Improved search and sort controls positioning under navigation tabs",
            "Better visual hierarchy with consistent spacing and layout"
          ]
        },
        {
          title: "👥 Enhanced Guest Player Workflow",
          description: "Completely revamped guest player creation and management system.",
          features: [
            "Guest players automatically get 'signed up' status when added to session",
            "No page refresh needed - guests appear immediately in player management",
            "Fixed database integration with proper session_players table usage",
            "Correct status display with proper counts in 'All' tab"
          ]
        },
        {
          title: "🎮 Improved Game Management",
          description: "Better handling of unbalanced games and match results.",
          features: [
            "Fixed unbalanced game warning messages to show proper game type instead of '[object Promise]'",
            "Replaced intrusive notifications with subtle hover tooltips",
            "Added 'No ELO change' indicators with explanatory tooltips",
            "Improved async function handling for better performance"
          ]
        },
        {
          title: "👤 Standardized Player Display",
          description: "Consistent player gender indicators and status management across the platform.",
          features: [
            "Standardized female player indicators with pink color and ♀ symbol",
            "Updated all player management components for consistent styling",
            "Fixed 'All' tab to show only players actually in the session",
            "Improved status badge display for better user understanding"
          ]
        },
        {
          title: "🔧 Database & Technical Improvements",
          description: "Enhanced database functions and resolved technical issues.",
          features: [
            "Added missing database functions including update_player_elo_and_history",
            "Fixed ELO rating updates and history logging functionality",
            "Resolved database schema conflicts and migration issues",
            "Improved error handling and user feedback throughout the system"
          ]
        }
      ]
    },
    {
      version: "v1.2.0",
      date: "September 2025",
      title: "Enhanced Session Management & User Experience",
      highlights: [
        {
          title: "🎯 Improved Session Signup Flow",
          description: "Complete redesign of the session signup experience with better visual feedback and confirmation modals.",
          features: [
            "Smart button states: 'Sign Up', 'Signed Up ✓', 'Waitlist #X', 'Sign Up Again'",
            "Confirmation modal for cancelling signups with clear messaging",
            "Reusable CancelSignupModal component for consistent UX",
            "Proper handling of waitlist vs confirmed signup states"
          ]
        },
        {
          title: "📊 Session Monitoring Dashboard",
          description: "New comprehensive session monitoring page for post-session management.",
          features: [
            "Time-based organization (Today, Yesterday, This Week, Older)",
            "Enhanced session cards with progress indicators and action items",
            "Quick action dashboard with summary statistics",
            "Smart filtering and search functionality with dropdown filters",
            "Development banner indicating feature status"
          ]
        },
        {
          title: "🔄 Cancelled Signup History",
          description: "Transparency improvements for session signup management.",
          features: [
            "Cancelled signups now preserved instead of deleted",
            "View cancelled players in session details modal",
            "Visual distinction for cancelled players (grayed out with X icon)",
            "Allow re-signup after cancellation with 'Sign Up Again' button",
            "Maintains complete signup history for each session"
          ]
        },
        {
          title: "🎨 Design System Improvements",
          description: "Enhanced UI consistency and user experience across the platform.",
          features: [
            "Reusable SessionCardBase component for consistent session cards",
            "Improved button variants and styling",
            "Better visual hierarchy and spacing",
            "Consistent modal designs and interactions",
            "Enhanced filter panels with design system components"
          ]
        },
        {
          title: "🔧 Technical Improvements",
          description: "Backend and infrastructure enhancements for better reliability.",
          features: [
            "Fixed UUID handling in signup functions",
            "Improved error handling and user feedback",
            "Enhanced session status management",
            "Better data fetching and state management",
            "Optimized component reusability and maintainability"
          ]
        }
      ]
    },
    {
      version: "v1.1.0",
      date: "August 2025",
      title: "Session Management Foundation",
      highlights: [
        {
          title: "🏗️ Core Session System",
          description: "Built the foundation for session management and player signups.",
          features: [
            "Session creation and template system",
            "Player signup and waitlist management",
            "Session status tracking (draft, live, completed, archived)",
            "Basic session monitoring capabilities"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Heading level={1} className="text-3xl font-bold text-slate-900 mb-4">
            Release Notes
          </Heading>
          <Text className="text-lg text-slate-600 max-w-2xl mx-auto">
            Stay updated with the latest features and improvements to Shuttlr. 
            We're constantly working to enhance your badminton session management experience.
          </Text>
        </div>

        {/* Back Button */}
        <div className="mb-8">
          <BackButton onClick={() => navigate(-1)}>
            Back
          </BackButton>
        </div>

        {/* Release Notes */}
        <div className="space-y-8">
          {releases.map((release, index) => (
            <Card key={index} variant="default" size="large" className="overflow-hidden">
              <div className="p-6">
                {/* Release Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {release.version}
                      </span>
                      <span className="text-sm text-slate-500">{release.date}</span>
                    </div>
                    <Heading level={2} className="text-2xl font-bold text-slate-900">
                      {release.title}
                    </Heading>
                  </div>
                </div>

                {/* Highlights */}
                <div className="space-y-6">
                  {release.highlights.map((highlight, highlightIndex) => (
                    <div key={highlightIndex} className="border-l-4 border-blue-200 pl-6">
                      <Heading level={3} className="text-lg font-semibold text-slate-900 mb-2">
                        {highlight.title}
                      </Heading>
                      <Text className="text-slate-700 mb-3">
                        {highlight.description}
                      </Text>
                      <ul className="space-y-2">
                        {highlight.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                            <Text className="text-slate-600">{feature}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <Card variant="default" size="medium" className="bg-blue-50 border-blue-200">
            <div className="p-6">
              <Heading level={3} className="text-lg font-semibold text-blue-900 mb-2">
                🚀 What's Next?
              </Heading>
              <Text className="text-blue-800 mb-4">
                We're working on exciting new features including advanced analytics, 
                tournament management, and enhanced player matching algorithms.
              </Text>
              <Text className="text-sm text-blue-700">
                Have feedback or feature requests? We'd love to hear from you!
              </Text>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReleaseNotes;
